import { formatUserFacingError } from '@/lib/errors'
import { useState } from 'react'
import { storage, ref, uploadBytes, getDownloadURL } from '@/lib/firebase/storage'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { MemberPhotoCropModal } from './MemberPhotoCropModal'

export interface MemberPhotoPatch {
  squarePhotoUrl?: string
  portraitPhotoUrl?: string
}

interface MemberPhotoUploaderProps {
  /** Display slug used by admin uploads when no storageKey is supplied. */
  slug: string
  /** Stable member document ID used for linked-member self uploads. */
  storageKey?: string
  squarePhotoUrl: string
  portraitPhotoUrl: string
  /** Called after a successful Storage upload with whichever URLs changed. */
  onUploaded: (patch: MemberPhotoPatch) => void | Promise<void>
  /** Optional gating message shown next to the button (e.g. "Enter a name first"). */
  disabled?: boolean
  disabledReason?: string
  helperText?: string
}

export function MemberPhotoUploader({
  slug,
  storageKey,
  squarePhotoUrl,
  portraitPhotoUrl,
  onUploaded,
  disabled = false,
  disabledReason,
  helperText,
}: MemberPhotoUploaderProps) {
  const [open, setOpen] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function handleCropSave(squareBlob: Blob | null, portraitBlob: Blob | null) {
    setError(null)
    const patch: MemberPhotoPatch = {}
    const pathSegment = storageKey ?? slug

    const tasks: Promise<void>[] = []
    if (squareBlob) {
      tasks.push(
        (async () => {
          const r = ref(storage, `company/${pathSegment}/square`)
          await uploadBytes(r, squareBlob)
          patch.squarePhotoUrl = await getDownloadURL(r)
        })(),
      )
    }
    if (portraitBlob) {
      tasks.push(
        (async () => {
          const r = ref(storage, `company/${pathSegment}/portrait`)
          await uploadBytes(r, portraitBlob)
          patch.portraitPhotoUrl = await getDownloadURL(r)
        })(),
      )
    }

    try {
      await Promise.all(tasks)
      if (Object.keys(patch).length > 0) await onUploaded(patch)
      setOpen(false)
    } catch (err) {
      setError(formatUserFacingError(err, 'Photo upload failed'))
    }
  }

  return (
    <>
      <div className="space-y-3">
        <div>
          <Label className="text-base font-semibold">Photos</Label>
          {helperText && (
            <p className="mt-0.5 text-xs text-muted-foreground">{helperText}</p>
          )}
        </div>

        <div className="space-y-3 rounded-lg border p-4">
          <div className="flex items-center justify-between gap-4">
            <div className="space-y-1">
              {squarePhotoUrl && (
                <p className="text-xs text-muted-foreground">Square: uploaded</p>
              )}
              {portraitPhotoUrl && (
                <p className="text-xs text-muted-foreground">Portrait: uploaded</p>
              )}
              {!squarePhotoUrl && !portraitPhotoUrl && (
                <p className="text-xs text-muted-foreground">No photos uploaded yet.</p>
              )}
            </div>
            <div className="flex items-center gap-2">
              {squarePhotoUrl && (
                <img
                  src={squarePhotoUrl}
                  alt="Square preview"
                  className="h-16 w-16 shrink-0 rounded-full border object-cover"
                />
              )}
              {portraitPhotoUrl && (
                <img
                  src={portraitPhotoUrl}
                  alt="Portrait preview"
                  className="h-20 w-11 shrink-0 rounded border object-cover"
                />
              )}
            </div>
          </div>

          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={disabled}
            onClick={() => setOpen(true)}
          >
            {squarePhotoUrl || portraitPhotoUrl ? 'Replace Photos' : 'Upload Photos'}
          </Button>
          {disabled && disabledReason && (
            <p className="text-xs text-muted-foreground">{disabledReason}</p>
          )}
          {error && <p className="text-sm text-destructive">{error}</p>}
        </div>
      </div>

      <MemberPhotoCropModal
        open={open}
        onClose={() => setOpen(false)}
        onSave={handleCropSave}
      />
    </>
  )
}
